import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { playAlert, refreshPush, unlockAudio } from "@/lib/alerts";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, ClipboardCheck, FolderOpen, ShieldCheck, LogOut, Bike, UserCircle, Plus, Wrench, Scale, Landmark, ListChecks, Radio, CalendarDays, CalendarCheck, Plane, KeyRound, CalendarClock, Inbox, Globe, Package, Siren, Building2, Hospital, BarChart3 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLES, repairOverdue } from "@/lib/constants";
import { MobileNav } from "@/components/MobileNav";
import { ThemeToggle } from "@/components/ThemeToggle";
import { api } from "@/lib/api";

export const NavBadge = ({ n, testid }) => (n > 0 ? <span data-testid={testid} className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1.5 text-[10px] font-bold text-white">{n}</span> : null);

export const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, f: "dashboard", end: true },
  { to: "/my", label: "My Status", icon: ShieldCheck, roles: ["rider"], end: true },
  { to: "/my-jobs", label: "My Jobs", icon: Package, f: "jobs", hide: ["trustee", "fleet_manager", "admin"] },
  { to: "/hospital", label: "Request Jobs", icon: Hospital, roles: ["hospital"] },
  { to: "/trustees", label: "Trustee Hub", icon: Landmark, f: "reports" },
  { to: "/control", label: "Control", icon: Radio, f: "control" },
  { to: "/dispatch", label: "Dispatch", icon: Siren, f: "jobs", lvl: 2 },
  { to: "/job-reports", label: "Job Reports", icon: BarChart3, f: "jobs", lvl: 2 },
  { to: "/rota", label: "Rota", icon: CalendarDays, f: "rota" },
  { to: "/riders", label: "Riders", icon: Bike, f: "riders" },
  { to: "/renewals", label: "Renewals", icon: CalendarClock, f: "riders", hide: ["rider"] },
  { to: "/assessments", label: "Assessments", icon: ClipboardCheck, f: "assessments", hide: ["rider"] },
  { to: "/slots", label: "Re-assess Slots", icon: CalendarClock, f: "conduct_assessments", lvl: 2 },
  { to: "/fleet", label: "Fleet", icon: Wrench, f: "fleet" },
  { to: "/bookings", label: "Bookings", icon: CalendarCheck, f: "bookings" },
  { to: "/availability", label: "Availability", icon: Plane, f: "availability" },
  { to: "/appeals", label: "Appeals", icon: Scale, f: "appeals" },
  { to: "/documents", label: "Documents", icon: FolderOpen, f: "documents" },
  { to: "/users", label: "Users", icon: Users, roles: ["admin"] },
  { to: "/applications", label: "Applications", icon: Inbox, roles: ["admin"] },
  { to: "/site-content", label: "Website Content", icon: Globe, roles: ["admin"] },
  { to: "/permissions", label: "Permissions", icon: KeyRound, roles: ["admin"] },
  { to: "/ride-setup", label: "Ride Setup", icon: ListChecks, f: "ride_setup", lvl: 3 },
  { to: "/hospitals", label: "Hospitals", icon: Building2, f: "jobs", lvl: 3 },
  { to: "/account", label: "Account", icon: UserCircle },
];

const MOBILE_FIRST = {
  controller: ["/dispatch", "/control", "/rota", "/my-jobs"], admin: ["/dashboard", "/dispatch", "/rota", "/riders"],
  rider: ["/my-jobs", "/rota", "/fleet", "/my"], assessor: ["/dashboard", "/assessments", "/riders", "/my-jobs"],
  trustee: ["/dashboard", "/trustees", "/dispatch", "/job-reports"], fleet_manager: ["/fleet", "/bookings", "/dispatch", "/rota"],
};

const visible = (n, user) => (!n.roles || n.roles.includes(user.role)) && !(n.hide || []).includes(user.role) && (!n.f || (user.permissions?.[n.f] || 0) >= (n.lvl || 1));

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`;

const Brand = () => (
  <div className="flex items-center gap-3">
    <img src="/brand/logo.png" alt="SCBB logo" className="h-10 w-10" />
    <div className="leading-tight">
      <div className="font-display text-base font-extrabold tracking-tight">SCBB</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Rider Assessments</div>
    </div>
  </div>
);

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const items = NAV.filter((n) => visible(n, user));
  const pri = MOBILE_FIRST[user.role] || [];
  const mobileItems = [...items].sort((a, b) => (pri.indexOf(a.to) + 1 || 99) - (pri.indexOf(b.to) + 1 || 99));
  const canAssess = (user.permissions?.conduct_assessments || 0) >= 2;
  const doLogout = () => { logout(); nav("/login"); };
  const [badges, setBadges] = useState({});
  const canRota = (user.permissions?.rota || 0) >= 2;
  const fleetAlerts = ["admin", "fleet_manager"].includes(user.role);
  useEffect(() => {
    if (!fleetAlerts) return undefined;
    const poll = () => api.get("/bikes").then((r) => setBadges((b) => ({ ...b, "/fleet": r.data.filter(repairOverdue).length }))).catch(() => {});
    poll();
    const t = setInterval(poll, 60000);
    window.addEventListener("fleet-changed", poll);
    return () => { clearInterval(t); window.removeEventListener("fleet-changed", poll); };
  }, [fleetAlerts, loc.pathname]);
  useEffect(() => {
    if (!canRota) return undefined;
    const poll = () => api.get("/rota/offers").then((r) => setBadges((b) => ({ ...b, "/rota": r.data.filter((o) => o.to_user_id === user.id).length }))).catch(() => {});
    poll();
    const t = setInterval(poll, 60000);
    window.addEventListener("offers-changed", poll);
    return () => { clearInterval(t); window.removeEventListener("offers-changed", poll); };
  }, [canRota, user.id, loc.pathname]);
  const canSlots = (user.permissions?.conduct_assessments || 0) >= 2;
  const canJobs = (user.permissions?.jobs || 0) >= 1;
  const prevCounts = useRef(null);
  useEffect(() => {
    if (!canJobs) return undefined;
    const unlock = () => unlockAudio();
    window.addEventListener("pointerdown", unlock, { once: true });
    refreshPush();
    return () => window.removeEventListener("pointerdown", unlock);
  }, [canJobs]);
  useEffect(() => {
    if (!canJobs) return undefined;
    const poll = () => api.get("/jobs/counts").then((r) => {
      const p = prevCounts.current;
      if (p && r.data.mine > p.mine) { playAlert(3); toast.warning("New job from control", { action: { label: "Open", onClick: () => nav("/my-jobs") }, duration: 15000 }); window.dispatchEvent(new Event("jobs-changed")); }
      else if (p && r.data.escalated > p.escalated) { playAlert(4); toast.error("A rider hasn't accepted a job after 5 minutes", { action: { label: "Dispatch", onClick: () => nav("/dispatch") }, duration: 30000 }); }
      else if (p && r.data.gaps > (p.gaps || 0)) { playAlert(1); toast.warning("Rota gap: a pre-booked job has nobody on the rota", { action: { label: "Dispatch", onClick: () => nav("/dispatch") }, duration: 20000 }); }
      else if (p && r.data.waiting > p.waiting) { playAlert(1); toast.info("New job request waiting", { action: { label: "Dispatch", onClick: () => nav("/dispatch") } }); }
      prevCounts.current = r.data;
      setBadges((b) => ({ ...b, "/my-jobs": r.data.mine, "/dispatch": r.data.waiting + (r.data.escalated || 0) }));
    }).catch(() => {});
    poll();
    const t = setInterval(poll, 10000);
    const own = () => { if (prevCounts.current) prevCounts.current = { ...prevCounts.current, waiting: prevCounts.current.waiting + 1 }; };
    window.addEventListener("job-self-created", own);
    return () => { clearInterval(t); window.removeEventListener("job-self-created", own); };
  }, [canJobs]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (user.role !== "admin") return undefined;
    const poll = () => api.get("/applications/count-new").then((r) => setBadges((b) => ({ ...b, "/applications": r.data.count }))).catch(() => {});
    poll();
    const t = setInterval(poll, 60000);
    window.addEventListener("apps-changed", poll);
    return () => { clearInterval(t); window.removeEventListener("apps-changed", poll); };
  }, [user.role, loc.pathname]);
  useEffect(() => {
    if (!canSlots) return undefined;
    const poll = () => api.get("/assessment-slots/unseen").then((r) => setBadges((b) => ({ ...b, "/slots": r.data.count }))).catch(() => {});
    poll();
    const t = setInterval(poll, 60000);
    window.addEventListener("slots-seen", poll);
    return () => { clearInterval(t); window.removeEventListener("slots-seen", poll); };
  }, [canSlots, loc.pathname]);

  return (
    <div className="flex min-h-screen">
      <aside className="no-print sticky top-0 hidden h-screen w-64 shrink-0 flex-col gap-4 border-r bg-card p-4 lg:flex">
        <div className="flex min-h-0 flex-1 flex-col gap-6">
          <Brand />
          {canAssess && (
            <button data-testid="sidebar-new-assessment-btn" onClick={() => nav("/assess/new")} className="flex w-full items-center justify-center gap-2 rounded-full bg-destructive px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-red-700">
              <Plus className="h-4 w-4" /> New Check Ride
            </button>
          )}
          <nav className="-mr-2 min-h-0 flex-1 space-y-1 overflow-y-auto pr-2" data-testid="sidebar-nav">
            {items.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className={linkCls} data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}>
                <n.icon className="h-4 w-4" /> {n.label}<NavBadge n={badges[n.to]} testid={`nav-badge-${n.label.toLowerCase().replace(/ /g, "-")}`} />
              </NavLink>
            ))}
          </nav>
        </div>
        <div className="space-y-3 border-t pt-4">
          <div className="text-sm">
            <div data-testid="current-user-name" className="font-semibold">{user.name}</div>
            <div className="text-xs text-muted-foreground">{ROLES[user.role]}</div>
          </div>
          <div className="flex items-center justify-between">
            <button data-testid="logout-btn" onClick={doLogout} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
            <ThemeToggle testid="sidebar-theme-toggle" />
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="no-print sticky top-0 z-40 flex items-center justify-between border-b bg-background/85 px-4 py-3 backdrop-blur-xl lg:hidden">
          <Brand />
          <div className="flex items-center gap-1">
            <ThemeToggle testid="mobile-theme-toggle" />
            <button data-testid="mobile-logout-btn" onClick={doLogout} className="rounded-full p-2 text-muted-foreground hover:bg-secondary" aria-label="Sign out">
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </header>
        <div className="hivis-stripe no-print h-1.5 w-full opacity-90" />
        <main className="mx-auto w-full max-w-7xl flex-1 px-4 pb-28 pt-6 sm:px-6 lg:px-10 lg:pb-12 lg:pt-10">
          <Outlet />
        </main>
      </div>

      <MobileNav items={mobileItems} badges={badges} />
      {canAssess && ["/dashboard", "/riders", "/assessments"].includes(loc.pathname) && (
        <button data-testid="mobile-new-assessment-fab" onClick={() => nav("/assess/new")} className="no-print fixed bottom-20 right-4 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-destructive text-white shadow-[0_8px_30px_rgba(219,0,0,0.45)] lg:hidden" aria-label="New check ride">
          <Plus className="h-6 w-6" />
        </button>
      )}
    </div>
  );
}
